// src/context/AuthContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { logAction } from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const logout = async () => {
        try {
            if (currentUser) {
                await logAction(
                    currentUser.uid,
                    'Logout do sistema',
                    { email: currentUser.email, nome: currentUser.nome },
                    currentUser.email,
                    currentUser.idOrganizacao || 'GLOBAL'
                );
            }
            await signOut(auth);
        } catch (error) {
            console.error("[AuthContext] Erro no logout:", error);
        }
    };

    const resetPassword = (email) => {
        return sendPasswordResetEmail(auth, email);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    const userDocRef = doc(db, "usuarios", user.uid);
                    const userDoc = await getDoc(userDocRef);

                    if (userDoc.exists()) {
                        const userProfileData = {
                            uid: user.uid,
                            ...userDoc.data()
                        };

                        // Buscar dados da organização, se aplicável
                        if (userProfileData.idOrganizacao) {
                            try {
                                const orgDocRef = doc(db, "organizacoes", userProfileData.idOrganizacao);
                                const orgDoc = await getDoc(orgDocRef);
                                if (orgDoc.exists()) {
                                    userProfileData.organization = {
                                        id: orgDoc.id,
                                        ...orgDoc.data()
                                    };
                                }
                            } catch (orgError) {
                                console.error("[AuthContext] Erro ao buscar organização:", orgError);
                            }
                        }

                        // Atualiza o último acesso
                        try {
                            await updateDoc(userDocRef, {
                                lastLogin: serverTimestamp()
                            });
                        } catch (error) {
                            console.error("[AuthContext] Erro ao atualizar lastLogin:", error);
                        }

                        setCurrentUser(userProfileData);
                    } else {
                        // Se o perfil não existe no Firestore, ainda assim define o usuário básico do Auth
                        console.warn(`[AuthContext] Nenhum perfil no Firestore para UID: ${user.uid}`);
                        setCurrentUser(user);
                    }
                } else {
                    setCurrentUser(null);
                }
            } catch (error) {
                // Se houver qualquer erro (ex: regras de segurança), desloga o usuário e registra o erro
                console.error("[AuthContext] Erro ao buscar perfil do usuário:", error);
                setCurrentUser(null);
            } finally {
                // ✅ GARANTIA: setLoading(false) será chamado mesmo se ocorrer um erro no 'try'
                setLoading(false);
            }
        });

        return unsubscribe;
    }, []);

    const refreshUserProfile = async () => {
        if (!auth.currentUser) return null;
        try {
            const userDocRef = doc(db, "usuarios", auth.currentUser.uid);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
                const userProfileData = {
                    uid: auth.currentUser.uid,
                    ...userDoc.data()
                };
                if (userProfileData.idOrganizacao) {
                    try {
                        const orgDocRef = doc(db, "organizacoes", userProfileData.idOrganizacao);
                        const orgDoc = await getDoc(orgDocRef);
                        if (orgDoc.exists()) {
                            userProfileData.organization = {
                                id: orgDoc.id,
                                ...orgDoc.data()
                            };
                        }
                    } catch (orgError) {
                        console.error("[AuthContext] Erro ao buscar organização no refresh:", orgError);
                    }
                }
                setCurrentUser(userProfileData);
                return userProfileData;
            }
        } catch (error) {
            console.error("[AuthContext] Erro no refreshUserProfile:", error);
        }
        return null;
    };

    const value = {
        currentUser,
        loading,
        login,
        logout,
        resetPassword,
        refreshUserProfile,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
